import type { Appointment as FhirAppointment, EpisodeOfCare, Patient, Practitioner } from '@medplum/fhirtypes';
import { getPatientIsHighRisk } from '@unify/fhir-model/resources/patient';
import { formatPractitionerStaffLabel } from '@unify/fhir-model/resources/practitioner';
import { mappedCaseloadClientSchema } from '@/schemas/caseload-client-mapped.schema';
import { displayLabelForEpisodeType } from './fhirValueSets/sessionAndEpisodeTypes';
import {
  getEpisodeOfCareGroupRef,
  isGroupTherapyEpisodeOfCare,
} from './groupTherapy';
import type { Client, ClientEpisode, EpisodeOfCareStatus } from '../pages/caseload/types';

function humanName(patient: Patient): { first: string; last: string; display: string } {
  const n = patient.name?.[0];
  const first = n?.given?.[0] ?? 'Client';
  const last = n?.family ?? '';
  const display = [first, last].filter(Boolean).join(' ').trim() || 'Client';
  return { first, last, display };
}

/**
 * Map FHIR EpisodeOfCare.status → caseload UI status.
 * Group Therapy `onhold` stays distinct (`on-hold`); other episode types keep legacy `follow-up`.
 */
function fhirEpisodeStatusToUi(
  status: string | undefined,
  isGroupTherapy: boolean,
): EpisodeOfCareStatus {
  switch (status) {
    case 'planned':
      return 'planned';
    case 'waitlist':
      return 'waitlist';
    case 'active':
      return 'active';
    case 'finished':
    case 'cancelled':
    case 'entered-in-error':
      return 'finished';
    case 'onhold':
      return isGroupTherapy ? 'on-hold' : 'follow-up';
    default:
      return 'active';
  }
}

function practitionerDisplay(ref: { reference?: string; display?: string } | undefined): string {
  if (ref?.display) return ref.display;
  if (ref?.reference?.startsWith('Practitioner/')) {
    return ref.reference.replace('Practitioner/', '');
  }
  return 'Provider';
}

export function mapEpisodeOfCareToClientEpisode(
  eoc: EpisodeOfCare,
  fallbackProvider: string,
): ClientEpisode {
  const start = eoc.period?.start?.slice(0, 10) ?? '2026-01-01';
  const end = eoc.period?.end?.slice(0, 10);
  const name = displayLabelForEpisodeType(eoc.type?.[0]);
  const provider = fallbackProvider || practitionerDisplay(eoc.careManager);
  const careManagerRef = eoc.careManager?.reference;
  const careManagerPractitionerId = careManagerRef?.startsWith('Practitioner/')
    ? careManagerRef.slice('Practitioner/'.length)
    : undefined;
  const isGroupTherapy = isGroupTherapyEpisodeOfCare(eoc);
  const groupRef = getEpisodeOfCareGroupRef(eoc);
  return {
    id: eoc.id ?? 'episode',
    name,
    status: fhirEpisodeStatusToUi(eoc.status, isGroupTherapy),
    startDate: start,
    endDate: end,
    provider: provider || fallbackProvider,
    ...(careManagerPractitionerId ? { careManagerPractitionerId } : {}),
    ...(groupRef?.display ? { groupCohortName: groupRef.display } : {}),
    ...(groupRef?.reference ? { groupCohortReference: groupRef.reference } : {}),
  };
}

function pickAppointmentDates(appointments: FhirAppointment[]): {
  firstSession: string;
  mostRecent: string;
  next: string;
} {
  const dates: string[] = [];
  for (const a of appointments) {
    const s = a.start;
    if (s) dates.push(s);
  }
  dates.sort();
  const firstSession = dates[0]?.slice(0, 10) ?? '';
  const mostRecent = dates[dates.length - 1]?.slice(0, 10) ?? firstSession;
  const now = Date.now();
  const future = dates.filter((d) => new Date(d).getTime() >= now);
  const next = future[0]?.slice(0, 10) ?? mostRecent;
  return { firstSession: firstSession || mostRecent, mostRecent, next };
}

export interface MapPilotPatientToClientOptions {
  /** Logged-in Practitioner id (no prefix). Used with viewerIsSupervisor to classify My Team vs My Clients. */
  viewerPractitionerId?: string;
  /** When true and the episode care manager is another practitioner, row is My Team (Supervisee). */
  viewerIsSupervisor?: boolean;
}

// TODO: Transition caseload from patient-centric to episode-centric model
// PRD: Each caseload row should represent an EpisodeOfCare, not a Patient aggregate.
// Current: One row per patient with episodes nested. Target: One row per episode with
// columns for service type, care manager, status, last appointment.
// See PRD_EPISODES_OF_CARE_FHIR_MAPPING.md

/**
 * Map Medplum Patient + EpisodeOfCare + Appointments to Caseload `Client`.
 *
 * Supervisors: patients whose EoC `careManager` is not the supervisor are tagged
 * `relationshipType: 'Supervisee'` and `supervisee: <care manager from first sorted episode>`.
 * When a patient has multiple EoCs with different care managers, use **per-episode**
 * `ClientEpisode.provider` for filtering (see `episodeRowMatchesProviderFilter` in caseloadUtils).
 * Primary providers: care manager is self → `Primary`.
 */
export function mapPilotPatientToClient(
  patient: Patient,
  episodes: EpisodeOfCare[],
  appointments: FhirAppointment[],
  practitionerByRef: Map<string, string>,
  options?: MapPilotPatientToClientOptions,
): Client {
  const { first, last, display } = humanName(patient);
  const dob = patient.birthDate;
  const email = patient.telecom?.find((t) => t.system === 'email')?.value;
  const phone = patient.telecom?.find((t) => t.system === 'phone')?.value;
  const pronouns =
    patient.extension?.find((e) => e.url === 'https://unify.health/fhir/pronouns')?.valueString ?? '';
  const viewerId = options?.viewerPractitionerId;
  /** Prefer the viewer’s own EpisodeOfCare first so Primary / supervisee labels match dual-EoC patients. */
  const sortedEpisodes =
    viewerId != null
      ? [...episodes].sort((a, b) => {
          const aSelf = a.careManager?.reference === `Practitioner/${viewerId}` ? 0 : 1;
          const bSelf = b.careManager?.reference === `Practitioner/${viewerId}` ? 0 : 1;
          if (aSelf !== bSelf) {
            return aSelf - bSelf;
          }
          return (a.id ?? '').localeCompare(b.id ?? '');
        })
      : episodes;

  const clientEpisodes = sortedEpisodes.map((e) => {
    const cm = e.careManager?.reference;
    const fallback =
      (cm ? practitionerByRef.get(cm) : undefined) ??
      practitionerDisplay(e.careManager) ??
      'Provider';
    return mapEpisodeOfCareToClientEpisode(e, fallback);
  });
  const primaryPractitioner =
    sortedEpisodes.length === 0
      ? ''
      : sortedEpisodes[0]?.careManager?.reference != null
        ? practitionerByRef.get(sortedEpisodes[0].careManager!.reference!) ??
          practitionerDisplay(sortedEpisodes[0].careManager) ??
          ''
        : (() => {
            const episodeProvider = clientEpisodes[0]?.provider;
            return episodeProvider && episodeProvider !== 'Provider' ? episodeProvider : '';
          })();

  const careManagerRef = sortedEpisodes[0]?.careManager?.reference;
  const viewerIsSupervisor = options?.viewerIsSupervisor === true;
  const careManagerIsViewer =
    viewerId != null && careManagerRef != null && careManagerRef === `Practitioner/${viewerId}`;

  let relationshipType: Client['relationshipType'] = 'Primary';
  let supervisee: string | undefined;
  if (viewerIsSupervisor && !careManagerIsViewer && careManagerRef?.startsWith('Practitioner/')) {
    relationshipType = 'Supervisee';
    supervisee = primaryPractitioner;
  }

  const { firstSession, mostRecent, next } = pickAppointmentDates(appointments);
  const minor =
    dob != null
      ? (() => {
          const [y, m, d] = dob.split('-').map(Number);
          const born = new Date(y, (m ?? 1) - 1, d ?? 1);
          const age =
            (Date.now() - born.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
          return age < 18;
        })()
      : false;

  const mapped: Client = {
    id: patient.id ?? '',
    name: display,
    firstName: first,
    lastName: last,
    preferredFirstName: first,
    preferredLastName: last,
    pronouns,
    dateOfBirth: dob,
    clientType: minor ? 'Minor' : 'Adult',
    status: 'Active',
    relationshipType,
    firstSession: firstSession || clientEpisodes[0]?.startDate || '—',
    mostRecentAppointment: mostRecent || '—',
    nextAppointment: next || '—',
    outstandingItems: [],
    provider: primaryPractitioner,
    primaryProvider: primaryPractitioner,
    ...(supervisee != null ? { supervisee } : {}),
    practice: sortedEpisodes[0]?.managingOrganization?.display ?? 'Unify Mental Health',
    phone,
    email,
    episodes: clientEpisodes.length > 0 ? clientEpisodes : undefined,
    serviceType: (displayLabelForEpisodeType(sortedEpisodes[0]?.type?.[0]) ||
      'OP') as Client['serviceType'],
    ...(getPatientIsHighRisk(patient) ? { highRiskFlags: ['High Risk'] } : {}),
  };

  const parsed = mappedCaseloadClientSchema.safeParse(mapped);
  if (!parsed.success) {
    console.warn('[mapPilotPatientToClient] mapped client failed validation', parsed.error.flatten(), {
      patientId: patient.id,
    });
  }
  return mapped;
}

export function buildPractitionerNameMap(
  practitioners: Practitioner[],
): Map<string, string> {
  const m = new Map<string, string>();
  for (const p of practitioners) {
    const label = formatPractitionerStaffLabel(p) || p.id;
    if (p.id) {
      m.set(`Practitioner/${p.id}`, label ?? p.id);
    }
  }
  return m;
}
